import { Component, OnInit, OnDestroy, Input } from '@angular/core';

import { EMPTY, Observable, Subscription, combineLatest, forkJoin, of } from 'rxjs';
import { filter, switchMap, tap, catchError } from 'rxjs/operators';
import moment from 'moment-timezone';
import { MessageService } from 'primeng/api';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { UsersService } from '../../../../Core/Users/services/users.service';
import { TemplateRowModel } from '../../model/template-row.model';
import { TableColumnModel } from '../../../Forms/models/table-column.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { templateColumnsConfig } from './config/template-columns.config';
import { templateNameColumnConfig } from './config/template-name-column.config';
import { BriefingListItemModel } from '../../../MonitorShared/model/briefing-list-item.model';
import { DistributionTemplateService } from '../../../TargetingShared/services/distribution-template.service';
import { DistributionSummaryModel } from '../../../TargetingShared/model/distribution-summary-model';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { ConfirmDialogModel } from '../../../Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { BriefingTemplateService } from '../../services/briefing-template.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { AuditableModel } from '../../../Misc/models/auditable.model';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { DistributionModel } from '@Discover/Targeting/models/distribution.model';
import { UserModel } from '@Core/Users/models/user.model';
import { sortByDate } from '../../../Misc/helpers/sort/sort.helper';


type Template = BriefingListItemModel | DistributionSummaryModel;

/**
 * This component works as a template center which includes all templates including briefing, distribution and maybe future wire templates at the same time
 * Currently we use instanceof to determine which row template its type is to decide using which template service
 * In future, if we want to add more template types, we could create a general template service that includes all individual template service
 */
@Component({
    selector: 'agpr-manage-template-dialog',
    templateUrl: './manage-template-dialog.component.html',
    styleUrls: ['./manage-template-dialog.component.scss'],
    standalone: false
})
export class ManageTemplateDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    hideSlideOverlay: boolean = false;

    templateRows: TemplateRowModel[] = [];
    hasSelectedTemplates: boolean = false;
    tableLoading: boolean = false;

    private dateFormat: string;
    private subscriptions: Subscription[] = [];

    readonly id = 'manageTemplateDialog';
    readonly buttonTypes = ButtonTypesEnum;
    readonly templateColumnDefs: TableColumnModel[] = templateColumnsConfig;
    readonly primaryColumnDef: TableColumnModel = templateNameColumnConfig;

    constructor(
        public coreDialogService: CoreDialogService,
        private briefingTemplateService: BriefingTemplateService,
        private distributionTemplateService: DistributionTemplateService,
        private localizationService: LocalizationService,
        private usersService: UsersService,
        private messageService: MessageService,
        private userStateService: CurrentUserStateService
    ) {
        super(coreDialogService, 'ManageTemplate');
    }

    ngOnInit() {
        this.subscriptions.push(
            combineLatest([
                this.distributionTemplateService.all$,
                this.briefingTemplateService.all$,
                this.usersService.accountUsers$,
                this.userStateService.getPreferences().pipe(filter(x => !!x))
            ]).subscribe(([distTemplates, briefingTemplates, users, prefs]) => {

                this.dateFormat = prefs.getDateFormatString(true);

                this.templateRows = [...distTemplates, ...briefingTemplates]
                    .map(template => {
                        const templateRow = new TemplateRowModel(template.clone());
                        templateRow.tooltipInfo = this.buildTooltipInfo(template, users);

                        return templateRow;
                    }) ?? [];

                this.checkForSelected();
            })
        );
    }

    updateTemplate(templateRow: TemplateRowModel) {
        const oldName = templateRow.template.name;
        if (templateRow.name === templateRow.template.name || !templateRow.name.trim()) {
            templateRow.name = oldName;
            return;
        }

        templateRow.template.name = templateRow.name;

        this.templateRows = [];

        if (templateRow.template instanceof DistributionSummaryModel) {

            this.distributionTemplateService.getById(templateRow.template.id).pipe(
                switchMap((template: DistributionModel) => {
                    template.name = templateRow.template.name;
                    return this.distributionTemplateService.update(template);
                }),
                catchError((error) => {
                    // revert the displayed value
                    templateRow.name = oldName;
                    templateRow.template.name = oldName;

                    throw error;
                })
            ).subscribe();

        } else {

            this.briefingTemplateService.getById(templateRow.template.id).pipe(
                switchMap(template => {
                    template.name = templateRow.template.name;
                    return this.briefingTemplateService.update(template);
                }),
                catchError((error) => {
                    // revert the displayed value
                    templateRow.name = oldName;
                    templateRow.template.name = oldName;

                    throw error;
                })
            ).subscribe();
        }
    }

    deleteSelectedTemplates() {
        const selectedTemplates = this.templateRows
            .filter(templateRow => templateRow.selected)
            .map(templateRow => templateRow.template);

        this.openDeleteConfirmationDialog(selectedTemplates);
    }

    deleteTemplate(templateRow: TemplateRowModel) {
        this.openDeleteConfirmationDialog([templateRow.template]);
    }

    private openDeleteConfirmationDialog(templates: Template[]) {
        const io: ConfirmDialogModel = {
            inputs: {
                id: 'TemplateDelete',
                heading: 'Briefings.templateManageDialog.deleteTemplate',
                alertType: messageSeverityEnum.error,
                alertMessage: 'Briefings.templateManageDialog.confirmDeleteWarningMessage',
                message: 'Briefings.templateManageDialog.confirmDeleteMessage',
                list: templates.map(item => item.name),
                okLabel: 'Core.button.delete',
                confirmAction: () => {

                    if (templates.length === 0) {
                        return of(null);
                    }

                    this.tableLoading = true;

                    return combineLatest(
                        templates.map(template => {
                            if (template instanceof DistributionSummaryModel) {
                                return this.distributionTemplateService.delete(template, false);
                            } else if (template instanceof BriefingListItemModel) {
                                return this.briefingTemplateService.delete(template, false);
                            }
                            return EMPTY;
                        })
                    ).pipe(
                        switchMap(() => {
                            const refreshCalls: Observable<unknown>[] = [];

                            if (templates.some(t => t instanceof DistributionSummaryModel)) {
                                refreshCalls.push(this.distributionTemplateService.refreshAllManual());
                            }
                            if (templates.some(t => t instanceof BriefingListItemModel)) {
                                refreshCalls.push(this.briefingTemplateService.refreshAllManual());
                            }

                            return forkJoin(refreshCalls);
                        }),
                        tap(() => {
                            if (templates.length === 1) {
                                this.messageService.add({
                                    severity: messageSeverityEnum.success,
                                    summary: 'Common.template.delete.heading',
                                    detail: this.localizationService.transform('Common.template.delete.message', {
                                        name: templates[0].name
                                    })
                                });
                            } else {
                                this.messageService.add({
                                    severity: messageSeverityEnum.success,
                                    summary: 'Common.template.bulkDelete.heading',
                                    detail: this.localizationService.transform('Common.template.bulkDelete.message', {
                                        number: templates.length
                                    })
                                });
                            }

                            this.tableLoading = false;
                            this.checkForSelected();
                        }),
                        catchError((error) => {
                            if (templates.length === 1) {
                                this.messageService.add({
                                    severity: messageSeverityEnum.error,
                                    summary: 'Common.template.delete.error.heading',
                                    detail: this.localizationService.transform('Common.template.delete.error.message', {
                                        name: templates[0].name
                                    })
                                });
                            } else {
                                this.messageService.add({
                                    severity: messageSeverityEnum.error,
                                    summary: 'Common.template.bulkDelete.error.heading',
                                    detail: this.localizationService.transform('Common.template.bulkDelete.error.message', {
                                        number: templates.length
                                    })
                                });
                            }

                            this.tableLoading = false;
                            this.checkForSelected();

                            throw error;
                        })
                    );
                }
            },
            outputs: {
                dialogClose: () => {}
            }
        };

        this.coreDialogService.open(ConfirmDialogComponent, io);
    }


    cancel() {
        this.hideSlideOverlay = true;
        this.closeDialog();
    }

    checkForSelected() {
        this.hasSelectedTemplates = this.templateRows.some(x => x.selected);
    }

    private buildTooltipInfo(template: AuditableModel, users: UserModel[]): string {

        const findUserName = (id: number) => users.find(u => u.id === id)?.getDisplayName() ?? null;

        return this.localizationService.transform('Briefings.templateManageDialog.tooltipInfo', {
            createdBy: findUserName(template.createdByUserId),
            createdDate: moment(template.createdDate).format(this.dateFormat),
            modifiedBy: findUserName(template.modifiedByUserId ? template.modifiedByUserId : template.createdByUserId),
            modifiedDate: moment(template.modifiedDate ? template.modifiedDate : template.createdDate).format(this.dateFormat)
        });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
